import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { api, setAuthTokenProvider } from './api.js';
import { AuthContext } from './authContextObject.js';
import { getFirebaseAuth, isFirebaseConfigured, describeAuthError } from './firebase.js';

/**
 * AuthContext — admin session state.
 *
 * Two facts are tracked separately and both matter:
 *
 *   user   — a verified Firebase identity (authentication)
 *   isAdmin — that identity is authorised to manage the catalogue
 *
 * The second is decided by the server, not the client. After sign-in we call
 * /api/admin/session; a 403 means "signed in but not an admin", which is a
 * genuinely different state from "signed out" and gets its own message. The
 * client never grants itself admin rights.
 */

const INITIAL = {
  status: 'loading', // loading | anonymous | forbidden | ready
  user: null,
  capabilities: { imageUpload: false },
  error: null,
};

export function AuthProvider({ children }) {
  const [state, setState] = useState(isFirebaseConfigured ? INITIAL : { ...INITIAL, status: 'anonymous' });

  // Held in a ref so api.js can pull a fresh token without re-rendering anything.
  const firebaseUserRef = useRef(null);

  /**
   * Register the token supplier used for every authenticated request.
   * getIdToken() refreshes automatically when the current token is near expiry,
   * so long admin sessions do not start failing after an hour.
   */
  useEffect(() => {
    setAuthTokenProvider(async () => {
      const current = firebaseUserRef.current;
      return current ? current.getIdToken() : null;
    });
  }, []);

  /** Ask the server whether the signed-in identity may administer. */
  const verifyAdmin = useCallback(async () => {
    try {
      const { user, capabilities } = await api.admin.session();
      setState({ status: 'ready', user, capabilities, error: null });
    } catch (err) {
      if (err.status === 403) {
        setState({ status: 'forbidden', user: null, capabilities: { imageUpload: false }, error: err.message });
      } else {
        setState({ status: 'anonymous', user: null, capabilities: { imageUpload: false }, error: err.message });
      }
    }
  }, []);

  // Subscribe to Firebase's session. onIdTokenChanged (rather than
  // onAuthStateChanged) also fires on token refresh, keeping the ref current.
  useEffect(() => {
    if (!isFirebaseConfigured) return undefined;

    let unsubscribe = () => {};
    let cancelled = false;

    (async () => {
      try {
        const auth = await getFirebaseAuth();
        const { onIdTokenChanged } = await import('firebase/auth');
        if (cancelled) return;

        unsubscribe = onIdTokenChanged(auth, async (firebaseUser) => {
          firebaseUserRef.current = firebaseUser;

          if (!firebaseUser) {
            setState({ status: 'anonymous', user: null, capabilities: { imageUpload: false }, error: null });
            return;
          }
          await verifyAdmin();
        });
      } catch (err) {
        if (!cancelled) {
          setState({ status: 'anonymous', user: null, capabilities: { imageUpload: false }, error: err.message });
        }
      }
    })();

    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, [verifyAdmin]);

  const signIn = useCallback(async (email, password) => {
    setState((prev) => ({ ...prev, status: 'loading', error: null }));
    try {
      const auth = await getFirebaseAuth();
      const { signInWithEmailAndPassword } = await import('firebase/auth');
      await signInWithEmailAndPassword(auth, email, password);
      // The onIdTokenChanged listener takes over and runs the admin check.
    } catch (err) {
      const message = describeAuthError(err);
      setState({ status: 'anonymous', user: null, capabilities: { imageUpload: false }, error: message });
      // Preserve the original Firebase error so the underlying code survives for
      // debugging, while the caller sees the human-readable message.
      throw new Error(message, { cause: err });
    }
  }, []);

  const signOut = useCallback(async () => {
    try {
      const auth = await getFirebaseAuth();
      const { signOut: firebaseSignOut } = await import('firebase/auth');
      await firebaseSignOut(auth);
    } finally {
      firebaseUserRef.current = null;
      setState({ status: 'anonymous', user: null, capabilities: { imageUpload: false }, error: null });
    }
  }, []);

  const value = useMemo(
    () => ({
      ...state,
      isAuthenticated: state.status === 'ready',
      isConfigured: isFirebaseConfigured,
      signIn,
      signOut,
    }),
    [state, signIn, signOut],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
